/**
 * FaultTreeViewRegistry
 *
 * Singleton registry that manages available fault tree view strategies.
 * New views are registered here and can be retrieved by type.
 */

import {FaultTreeViewType} from '../../../@types/fault-tree-view-type';
import {FaultTreeViewStrategy} from './fault-tree-view-strategy';
import {StandardFaultTreeView} from './standard-fault-tree-view';
import {BowtieFaultTreeView} from './bowtie-fault-tree-view';

/**
 * Registry for managing fault tree view strategies.
 * Uses the singleton pattern to ensure a single registry instance.
 */
class FaultTreeViewRegistry {
    private views: Map<FaultTreeViewType, FaultTreeViewStrategy> = new Map();
    private static instance: FaultTreeViewRegistry;
    private initialized = false;

    private constructor() {
        // Views are registered lazily to avoid circular dependency issues
    }

    /**
     * Get the singleton instance of the registry.
     */
    static getInstance(): FaultTreeViewRegistry {
        if (!FaultTreeViewRegistry.instance) {
            FaultTreeViewRegistry.instance = new FaultTreeViewRegistry();
        }
        return FaultTreeViewRegistry.instance;
    }

    /**
     * Ensure default views are registered.
     * Called lazily on first access to avoid circular dependencies.
     */
    private ensureInitialized(): void {
        if (this.initialized) return;
        this.initialized = true;

        // Register the default standard view
        this.register(new StandardFaultTreeView());

        // Register the bowtie view
        this.register(new BowtieFaultTreeView());

        // Future views can be registered here:
        // this.register(new CompactFaultTreeView());
    }

    /**
     * Register a new view strategy.
     * Can be used to add custom views at runtime.
     *
     * @param view The view strategy to register
     */
    register(view: FaultTreeViewStrategy): void {
        this.views.set(view.viewType, view);
    }

    /**
     * Get a view strategy by type.
     *
     * @param viewType The type of view to retrieve
     * @returns The view strategy
     * @throws Error if the view type is not registered
     */
    get(viewType: FaultTreeViewType): FaultTreeViewStrategy {
        this.ensureInitialized();

        const view = this.views.get(viewType);
        if (!view) {
            throw new Error(`Unknown fault tree view type: ${viewType}. Available types: ${this.getAvailableViewTypes().join(', ')}`);
        }
        return view;
    }

    /**
     * Get all registered view types.
     *
     * @returns Array of registered view types
     */
    getAvailableViewTypes(): FaultTreeViewType[] {
        this.ensureInitialized();
        return Array.from(this.views.keys());
    }

    /**
     * Get all registered view strategies.
     *
     * @returns Array of registered view strategies
     */
    getAvailableViews(): FaultTreeViewStrategy[] {
        this.ensureInitialized();
        return Array.from(this.views.values());
    }

    /**
     * Check if a view type is registered.
     *
     * @param viewType The view type to check
     * @returns true if the view type is registered
     */
    has(viewType: FaultTreeViewType): boolean {
        this.ensureInitialized();
        return this.views.has(viewType);
    }

    /**
     * Get the default view type.
     *
     * @returns The default view type (STANDARD)
     */
    getDefaultViewType(): FaultTreeViewType {
        return FaultTreeViewType.STANDARD;
    }
}

/**
 * Singleton instance of the fault tree view registry.
 * Use this to get and register view strategies.
 */
export const faultTreeViewRegistry = FaultTreeViewRegistry.getInstance();
